import { Trans } from '@lingui/react/macro'
import { t } from '@lingui/core/macro'
import filesize from 'filesize'
import moize from 'moize'
import { useLingui } from '@lingui/react'

import type { BrandAlias } from '@jotta/config/brand'
import type { GetPaymentInfoResponse } from '@jotta/grpc-connect-openapi/payment'
import { unbrandedColorPallette } from '@jotta/ui/DesignSystem'
import { useThemeContext } from '@jotta/ui/ThemeProvider'
import { selectDesignSystemColors } from '@jotta/ui/useDesignSystem'

import type { ExtendedBusinessUser } from '../BizApi'
import { bizApi, useBizUsers } from '../BizApi'
import type { BizPieChartData } from '../BizTypes'
import { BizActionButtonGetMoreStorage } from '../components/BizActionButtonGetMoreStorage'
import { BizPieChart } from '../components/BizPieChart'

import { BizWidget } from './BizWidget'

const selectUserData = moize.deep(
  (users: ExtendedBusinessUser[], brand: BrandAlias) => {
    const { colors } = selectDesignSystemColors(
      brand,
      ...unbrandedColorPallette,
    )
    const data: BizPieChartData = users
      .toSorted((a, b) =>
        Number(Number(b.storageBytes) - Number(a.storageBytes)),
      )
      .slice(0, 5)
      .map((user, i) => ({
        name: user.name || user.email || '',
        value: Number(user.storageBytes),
        fill: colors[i % colors.length],
      }))

    return data
  },
)

const selectPaymentData = moize.deep(
  (paymentInfo: GetPaymentInfoResponse, locale: string, brand: BrandAlias) => {
    const { colorMap } = selectDesignSystemColors(
      brand,
      ...unbrandedColorPallette,
    )
    const quotaBytes = Number(paymentInfo.subscriptionQuotaBytes)
    const usageBytes = Number(paymentInfo.subscriptionUsageBytes)
    const availableBytes = Math.max(0, quotaBytes - usageBytes)
    const quota = filesize(quotaBytes, {
      locale,
    })
    const usage = filesize(usageBytes, {
      locale,
    })
    return {
      quota,
      usage,
      chartData: [
        {
          name: t`Available`,
          value: availableBytes,
          fill: colorMap.unbranded_colors__blue,
        },
        {
          name: t`Used`,
          value: usageBytes,
          fill: colorMap.unbranded_colors__yellow,
        },
      ] satisfies BizPieChartData,
    }
  },
)

export function BizWidgetAccountStats() {
  const { data } = bizApi.subscription.getPaymentInfo.useSuspenseQuery()
  const { users } = useBizUsers()
  const locale = useLingui().i18n.locale
  const brand = useThemeContext().brandAlias
  const { quota, usage, chartData } = selectPaymentData(data, locale, brand)
  return (
    <>
      <h2 className="title-md col-span-full flex items-center gap-3">
        <span>
          <Trans>Storage</Trans>{' '}
          <small className="body-lg-sh text-grey4">
            {`${quota} ${t`Storage`} (${usage} ${t`used`})`}
          </small>
        </span>
        <BizActionButtonGetMoreStorage className="ml-auto" />
      </h2>
      <BizWidget title={t`Account stats`}>
        <div className="grid aspect-video">
          <BizPieChart data={chartData} />
        </div>
      </BizWidget>
      <BizWidget title={t`Storage by user`}>
        <div className="grid aspect-video">
          <BizPieChart data={selectUserData(users, brand)} />
        </div>
      </BizWidget>
    </>
  )
}
